<script setup>
/**
 * A backup is every transaction you have ever made, and every file attached,
 * leaving the device. It is a zip (stores/backup.js) — plain, or actually
 * encrypted: AES-256, not the legacy zip password that any laptop can pick
 * apart in seconds.
 *
 * Settings' Your data tab. `quiet` drops the explanation, for the cloud's
 * "back up before you move" box.
 */
import { computed, ref } from 'vue'
import Icon from '../ui/Icon.vue'
import { toast } from '../../stores/state'
import { count } from '../../stores/usage'
import { errorText, t } from '../../stores/i18n'
import { zipWithPassword } from '../../lib/securezip'
import { backupEntries, backupStem, downloadBackup, saveFile as save } from '../../stores/backup'

const password = ref('')
const repeat = ref('')
const busy = ref(false)
const error = ref('')

const stem = computed(backupStem)
const tooShort = computed(() => password.value.length > 0 && password.value.length < 6)
const mismatch = computed(() => repeat.value.length > 0 && password.value !== repeat.value)
const ready = computed(() => password.value.length >= 6 && password.value === repeat.value)

async function protectedBackup() {
  error.value = ''
  busy.value = true
  try {
    const { entries, missing } = await backupEntries()
    const archive = await zipWithPassword(entries, password.value)
    save(archive, `${stem.value}.zip`, 'application/zip')
    count('backup', 'protected')
    if (missing.length) toast(t('backup.filesMissing', { count: missing.length }), 'warn')
    else toast(t('backup.downloadedProtected'), 'good')
    password.value = ''
    repeat.value = ''
  } catch (err) {
    error.value = errorText(err)
  } finally {
    busy.value = false
  }
}

async function plainBackup() {
  busy.value = true
  error.value = ''
  try {
    const missing = await downloadBackup()
    count('backup', 'plain')
    if (missing.length) toast(t('backup.filesMissing', { count: missing.length }), 'warn')
    else toast(t('backup.downloaded'), 'good')
  } catch (err) {
    error.value = errorText(err)
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <div class="grid gap-4" data-backup-panel>
      <p class="text-[12.5px] text-faint">{{ t('backup.subtitle') }}</p>
      <div>
        <label class="label" for="bk-password">{{ t('backup.password') }}</label>
        <input
          id="bk-password"
          v-model="password"
          type="password"
          class="input"
          autocomplete="new-password"
          :placeholder="t('backup.passwordPlaceholder')"
          @keydown.enter.prevent="ready && protectedBackup()"
        />
        <p v-if="tooShort" class="mt-1.5 text-[12px] text-warn">{{ t('backup.passwordHint') }}</p>
      </div>

      <div>
        <label class="label" for="bk-repeat">{{ t('backup.again') }}</label>
        <input
          id="bk-repeat"
          v-model="repeat"
          type="password"
          class="input"
          autocomplete="new-password"
          @keydown.enter.prevent="ready && protectedBackup()"
        />
        <p v-if="mismatch" class="mt-1.5 text-[12px] text-warn">{{ t('backup.mismatch') }}</p>
      </div>

      <div class="rounded-xl border border-line-soft bg-raised/50 p-3.5 text-[12.5px] text-muted">
        <p class="mb-1 font-semibold text-text">{{ stem }}.zip</p>
        <p>{{ t('backup.aesNote') }}</p>
        <p class="mt-1.5">{{ t('backup.noWayBack') }}</p>
      </div>

      <p v-if="error" class="flex items-center gap-2 text-[13px] text-danger">
        <Icon name="alert" :size="15" /> {{ error }}
      </p>

      <div class="flex flex-wrap items-center gap-2">
        <button class="btn-primary" :disabled="!ready || busy" @click="protectedBackup">
          <Icon name="download" :size="16" />
          {{ busy ? t('backup.packing') : t('backup.backUp') }}
        </button>
        <button class="btn-ghost" :disabled="busy" @click="plainBackup">{{ t('backup.withoutPassword') }}</button>
      </div>
  </div>
</template>
